import React from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Briefcase, FolderOpen, IdCard, FileText, ArrowLeft, Sparkles } from "lucide-react";

// Mappa slug modulo → metadata (titolo, descrizione, icona, esempi di funzionalità)
const MODULES = {
  paghe: {
    label: "Paghe",
    icon: Briefcase,
    tagline: "Elaborazione cedolini e adempimenti mensili",
    description: "Calcolo cedolini, contributi, CU, F24 e adempimenti annuali direttamente collegati alle presenze già raccolte. I dati validati nel Modulo Presenze fluiscono automaticamente qui.",
    features: [
      "Cedolini mensili con anteprima e chiusura",
      "F24 e prospetti contributi INPS/INAIL",
      "Certificazione Unica e 770",
      "Integrazione diretta con banca ore e cause",
    ],
  },
  pratiche: {
    label: "Pratiche",
    icon: FolderOpen,
    tagline: "Gestione dossier clienti e scadenzario",
    description: "Un fascicolo digitale per ogni pratica lavorativa o consulenziale: assunzioni, cessazioni, contenziosi, con scadenze e stato di avanzamento.",
    features: [
      "Fascicoli con documenti allegati",
      "Scadenzario integrato",
      "Workflow personalizzabili",
      "Storico e audit trail per pratica",
    ],
  },
  hr: {
    label: "HR",
    icon: IdCard,
    tagline: "Anagrafica dipendenti e ciclo di vita",
    description: "Un'anagrafica ricca dei dipendenti: contratti, valutazioni, formazione, obiettivi. Il complemento naturale del modulo Presenze per un HR completo.",
    features: [
      "Fascicolo dipendente unificato",
      "Piani formativi e certificazioni",
      "Valutazioni periodiche e obiettivi",
      "Onboarding e offboarding assistiti",
    ],
  },
  documenti: {
    label: "Documenti",
    icon: FileText,
    tagline: "Archivio digitale conforme e ricercabile",
    description: "Un archivio centralizzato di tutti i documenti dello studio: contratti, buste paga, certificazioni, con ricerca avanzata e conservazione a norma.",
    features: [
      "Ricerca full-text con OCR",
      "Firme digitali integrate",
      "Retention policy e conservazione a norma",
      "Condivisione sicura con clienti",
    ],
  },
};


export default function ModuloPlaceholder() {
  const { slug } = useParams();
  const mod = MODULES[slug] || {
    label: "Modulo",
    icon: Sparkles,
    tagline: "In arrivo",
    description: "",
    features: [],
  };
  const Icon = mod.icon;

  return (
    <div className="max-w-3xl space-y-6" data-testid={`modulo-placeholder-${slug}`}>
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft size={14} /> Torna al Modulo Presenze
      </Link>

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
          Gruppo Impresa Suite
        </div>
        <div className="flex items-center gap-3 mt-2">
          <div className="w-12 h-12 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-center justify-center">
            <Icon size={26} strokeWidth={1.75} />
          </div>
          <div>
            <h1
              className="text-3xl font-bold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope" }}
            >
              Modulo {mod.label}
            </h1>
            <div className="text-sm text-slate-500 mt-0.5">{mod.tagline}</div>
          </div>
        </div>
      </div>

      <Card className="border-amber-200 bg-amber-50/60">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-amber-800 font-semibold">
            Prossimamente disponibile
          </div>
          <div className="text-sm text-amber-900 mt-1">
            Questo modulo è in fase di sviluppo. Sarà integrato con il Modulo Presenze
            che stai già utilizzando: gli stessi dipendenti, le stesse aziende,
            un&apos;unica interfaccia coerente.
          </div>
        </CardContent>
      </Card>

      {mod.description && (
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="text-sm text-slate-700 leading-relaxed">
              {mod.description}
            </div>
          </CardContent>
        </Card>
      )}

      {mod.features.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
              Funzionalità previste
            </div>
            <ul className="space-y-2">
              {mod.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-2 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="pt-2">
        <Button asChild variant="outline">
          <Link to="/">Torna al Modulo Presenze</Link>
        </Button>
      </div>
    </div>
  );
}
